export interface Options {
  enableAutoReload?: boolean | undefined;
  rootMargin?: string | undefined;
  threshold?: number | undefined;
  load?(element: Element): void;
  loaded?(element: Element): void;
}

/**
 *
 * @param {string} type
 *
 */
const support = (type: string) => window && window[type as keyof Window]

const validAttribute = ['data-iesrc', 'data-alt', 'data-src', 'data-srcset', 'data-background-image', 'data-toggle-class']

const defaultConfig: Options = {
  rootMargin: '0px',
  threshold: 0,
  enableAutoReload: false,
  load(element: Element) {
    if (element.nodeName.toLowerCase() === 'picture') {
      let img = element.querySelector('img')
      let append = false

      if (img === null) {
        img = document.createElement('img')
        append = true
      }

      if (element.getAttribute('data-alt')) {
        img.alt = element.getAttribute('data-alt')!;
      }

      if (append) {
        element.append(img)
      }
    }

    if (element.nodeName.toLowerCase() === 'video' && !element.getAttribute('data-src')) {
      const videoEl = element as HTMLVideoElement
      if (videoEl.children) {
        const childs = videoEl.children
        for (const child of Array.from(childs)) {
          const childSrc = child.getAttribute('data-src')
          if (childSrc) {
            (child as HTMLSourceElement).src = childSrc
          }
        }

        videoEl.load()
      }
    }

    if (element.getAttribute('data-poster')) {
      (element as HTMLVideoElement).poster = element.getAttribute('data-poster')!
    }

    if (element.getAttribute('data-src')) {
      (element as HTMLImageElement | HTMLVideoElement).src = element.getAttribute('data-src')!
    }

    if (element.getAttribute('data-srcset')) {
      element.setAttribute('srcset', element.getAttribute('data-srcset')!)
    }

    let backgroundImageDelimiter = ','
    if (element.getAttribute('data-background-delimiter')) {
      backgroundImageDelimiter = element.getAttribute('data-background-delimiter')!
    }

    const htmlEl = element as HTMLElement
    if (element.getAttribute('data-background-image')) {
      htmlEl.style.backgroundImage = `url('${element.getAttribute('data-background-image')!.split(backgroundImageDelimiter).join('\'),url(\'')}')`
    } else if (element.getAttribute('data-background-image-set')) {
      const imageSetLinks = element.getAttribute('data-background-image-set')!.split(backgroundImageDelimiter)
      let firstUrlLink = (imageSetLinks[0]!.substring(0, imageSetLinks[0]!.indexOf(' ')) || imageSetLinks[0]!) // Substring before ... 1x
      firstUrlLink = firstUrlLink.indexOf('url(') === -1 ? `url(${firstUrlLink})` : firstUrlLink
      if (imageSetLinks.length === 1) {
        htmlEl.style.backgroundImage = firstUrlLink
      } else {
        element.setAttribute('style', (element.getAttribute('style') || '') + `background-image: ${firstUrlLink}; background-image: -webkit-image-set(${imageSetLinks}); background-image: image-set(${imageSetLinks})`)
      }
    }

    if (element.getAttribute('data-toggle-class')) {
      element.classList.toggle(element.getAttribute('data-toggle-class')!)
    }
  },
  loaded() {},
}

function markAsLoaded(element: Element) {
  element.setAttribute('data-loaded', 'true')
}

function preLoad(element: HTMLElement) {
  if (element.getAttribute('data-placeholder-background')) {
    element.style.background = element.getAttribute('data-placeholder-background')!
  }
}

const isLoaded = (element: Element) => element.getAttribute('data-loaded') === 'true'

const onIntersection = (load: Options['load'], loaded: Options['loaded']) => (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
  entries.forEach(entry => {
    if (entry.intersectionRatio > 0 || entry.isIntersecting) {
      observer.unobserve(entry.target)

      if (!isLoaded(entry.target)) {
        load?.(entry.target)
        markAsLoaded(entry.target)
        loaded?.(entry.target)
      }
    }
  })
}

const onMutation = (load: Options['load']) => (entries: MutationRecord[]) => {
  entries.forEach(entry => {
    if (isLoaded(entry.target as Element) && entry.type === 'attributes' && validAttribute.indexOf(entry.attributeName as string) > -1) {
      load?.(entry.target as Element)
    }
  })
}

const getElements = (selector: Element | NodeList | string, root = document) => {
  if (selector instanceof Element) {
    return [selector]
  }

  if (selector instanceof NodeList) {
    return selector
  }

  return root.querySelectorAll(selector)
}

export default function(selector = '.lozad', options = {}) {
  const { rootMargin, threshold, enableAutoReload, load, loaded } = Object.assign({}, defaultConfig, options)
  const observed = new WeakSet<Element>()
  let observer: IntersectionObserver | null = null
  let mutationObserver: MutationObserver | null = null

  if (support('IntersectionObserver')) {
    observer = new IntersectionObserver(onIntersection(load, loaded), {
      root: document,
      rootMargin,
      threshold,
    })
  }

  if (support('MutationObserver') && enableAutoReload) {
    mutationObserver = new MutationObserver(onMutation(load))
  }

  const elements = getElements(selector)
  for (const el of elements) {
    preLoad(el as HTMLElement)
  }

  // MutationObserver to watch for new elements added to the DOM
  if (support('MutationObserver')) {
    const globalMutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            let elements: Element[];

            if ((node as Element).classList.contains('lozad')) {
              elements = [node as Element];
            }
            else {
              elements = (node as Element).querySelectorAll(selector) as unknown as Element[];
            }

            for (const el of elements) {
              if (observed.has(el)) {
                continue;
              }

              observed.add(el)

              if (observer) {
                if (mutationObserver && enableAutoReload) {
                  mutationObserver.observe(el, { subtree: true, attributes: true, attributeFilter: validAttribute })
                }

                observer.observe(el)
              }
              else {
                load && load(el)
                markAsLoaded(el)
                loaded && loaded(el)
              }
            }
          }
        })
      })
    })

    globalMutationObserver.observe(document.body, { childList: true, subtree: true })
  }

  return {
    observe() {
      const elements = getElements(selector)

      for (const el of elements) {
        if (isLoaded(el as Element) || observed.has(el as Element)) {
          continue
        }

        observed.add(el as Element)

        if (observer) {
          if (mutationObserver && enableAutoReload) {
            mutationObserver.observe(el, { subtree: true, attributes: true, attributeFilter: validAttribute })
          }

          observer.observe(el as Element)
          continue
        }

        load && load(el as Element)
        markAsLoaded(el as Element)
        loaded && loaded(el as Element)
      }
    },
    triggerLoad(element: Element) {
      if (isLoaded(element)) {
        return
      }

      load && load(element)
      markAsLoaded(element)
      loaded && loaded(element)
    },
    observer,
    mutationObserver,
  }
}
